import IssuerFilters from '../IssuerFilters';
import IssuerRow from '../IssuerRow';
import IssuerPagination from '../IssuerPagination';
import TableSkeleton from '../../../TableSkeleton'; 
import { FaExclamationTriangle, FaSyncAlt, FaBuilding, FaWifi } from 'react-icons/fa';
import { useIssuersSummary } from '../../../../service/Hooks/Issuers/useIssuersSummary';

const IssuersList = () => {
  const {
    issuers,
    loading,
    isSearching,
    error,
    pagination,
    filters,
    setFilters,
    handleResetFilters,
    handlePageChange
  } = useIssuersSummary();

  const handleRetry = () => {
    handlePageChange(pagination.pageNumber);
  };

  // ✅ Détection erreur de connexion
  const isConnectionError = error && (
    error.includes('Network') ||
    error.includes('Failed to fetch') ||
    error.includes('connexion') ||
    error.includes('ECONNABORTED')
  );

  // ✅ SKELETON au premier chargement UNIQUEMENT
  if (loading && issuers.length === 0 && !isSearching) {
    return (
      <div className="container-fluid px-0">
        <IssuerFilters
          filters={filters}
          onFilterChange={() => {}}
          onReset={handleResetFilters}
          loading={true}
          isSearching={false}
        />
        
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <div className="d-flex align-items-center gap-2">
              <FaBuilding className="text-primary" size={20} />
              <div>
                <h5 className="mb-0 fw-bold">Émetteurs</h5>
                <small className="text-muted">Chargement...</small>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <TableSkeleton columns={6} rows={10} showHeader={true} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      {/* Filtres */}
      <IssuerFilters
        filters={filters}
        onFilterChange={setFilters}
        onReset={handleResetFilters}
        loading={loading}
        isSearching={isSearching}
      />

      {/* Tableau des émetteurs */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
          <div className="d-flex align-items-center gap-2">
            <FaBuilding className="text-primary" size={20} />
            <div>
              <h5 className="mb-0 fw-bold">Émetteurs</h5>
              <small className="text-muted">
                {isSearching ? 'Recherche...' :
                 isConnectionError ? 'Mode hors ligne' :
                 `${pagination.totalCount} résultat${pagination.totalCount > 1 ? 's' : ''}`}
              </small>
            </div>
          </div>
          
          {/* Spinner discret pendant recherche */}
          {isSearching && (
            <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '14px', height: '14px' }}>
              <span className="visually-hidden">Recherche...</span>
            </div>
          )}
        </div>
        
        <div className="card-body p-0">
          {/* ✅ ERREUR DE CONNEXION - Dans le tableau */}
          {isConnectionError ? (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 fw-semibold">Émetteur</th>
                    <th className="fw-semibold">Contacts</th>
                    <th className="text-center fw-semibold">Titres émis</th>
                    <th className="text-center fw-semibold">Unités totales</th>
                    <th className="text-end fw-semibold">Valeur totale</th>
                    <th className="text-center fw-semibold">Dernière modification</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="alert alert-warning border-0 d-inline-flex align-items-center gap-3 mx-auto" style={{ maxWidth: '500px' }}>
                        <FaWifi className="fs-3 text-warning flex-shrink-0" />
                        <div className="text-start">
                          <h6 className="mb-1 fw-bold">Connexion interrompue</h6>
                          <p className="mb-2 small text-muted">Impossible de charger les données. Vérifiez votre connexion internet.</p>
                          <button 
                            className="btn btn-sm btn-warning d-flex align-items-center gap-2"
                            onClick={handleRetry}
                          >
                            <FaSyncAlt size={12} />
                            Réessayer
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : 
          
          /* ✅ ERREUR GÉNÉRIQUE - Dans le tableau */
          error ? (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 fw-semibold">Émetteur</th>
                    <th className="fw-semibold">Contacts</th>
                    <th className="text-center fw-semibold">Titres émis</th>
                    <th className="text-center fw-semibold">Unités totales</th>
                    <th className="text-end fw-semibold">Valeur totale</th>
                    <th className="text-center fw-semibold">Dernière modification</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="alert alert-danger border-0 d-inline-flex align-items-center gap-3 mx-auto" style={{ maxWidth: '500px' }}>
                        <FaExclamationTriangle className="fs-3 text-danger flex-shrink-0" />
                        <div className="text-start">
                          <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                          <p className="mb-2 small text-muted">{error}</p>
                          <button 
                            className="btn btn-sm btn-danger d-flex align-items-center gap-2"
                            onClick={handleRetry}
                          >
                            <FaSyncAlt size={12} />
                            Réessayer
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) :
          
          /* ✅ SKELETON pendant recherche */
          isSearching && issuers.length === 0 ? (
            <TableSkeleton columns={6} rows={5} showHeader={false} />
          ) :
          
          /* ✅ DONNÉES OU MESSAGE VIDE */
          (
            <div className="table-responsive">
              <table className="table table-hover table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-4 fw-semibold">Émetteur</th>
                    <th className="fw-semibold">Contacts</th>
                    <th className="text-center fw-semibold">Titres émis</th>
                    <th className="text-center fw-semibold">Unités totales</th>
                    <th className="text-end fw-semibold">Valeur totale</th>
                    <th className="text-center fw-semibold">Dernière modification</th>
                  </tr>
                </thead>
                <tbody>
                  {issuers.length > 0 ? (
                    issuers.map(issuer => (
                      <IssuerRow key={issuer.accountId} issuer={issuer} />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <div className="text-muted py-4">
                          <FaBuilding className="fs-1 opacity-50 mb-3" />
                          <h5 className="mt-3 mb-2 fw-bold">Aucun émetteur trouvé</h5>
                          <p className="mb-3 text-muted">
                            {filters.issuerName 
                              ? `Aucun émetteur ne correspond à "${filters.issuerName}"`
                              : 'Aucun émetteur dans le domaine sélectionné'
                            }
                          </p>
                          {filters.issuerName && (
                            <button 
                              className="btn btn-outline-primary btn-sm rounded-pill px-4"
                              onClick={handleResetFilters}
                            >
                              Afficher tous les émetteurs
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {issuers.length > 0 && pagination.totalPages > 1 && !error && (
            <div className="border-top">
              <IssuerPagination
                currentPage={pagination.pageNumber}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalCount}
                itemsPerPage={pagination.pageSize}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssuersList;